//#region Function Imports
import React, { useEffect, useState, useRef } from "react";
import { useRoute } from "@react-navigation/native";
import {
  SafeAreaView,
  Platform,
  StyleSheet,
  useWindowDimensions,
  FlatList,
  View,
  ScrollView,
  Image,
  Pressable,
  TouchableOpacity,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useNavigation } from "@react-navigation/native";
import CustomLabel from "../../components/CustomLabel/CustomLabel";
import MyLeavesScreen from "../SelfService/MyLeaves/MyLeavesScreen";
import LeaveApplicationApprovalScreen from "../SelfService/LAA/LeaveApplicationApprovalScreen";
import PersonalLeaveBalancesScreen from "../SelfService/MyLeaves/PersonalLeaveBalancesScreen";
import { useAccountContext } from "../../hooks/useAccountContext";
import { useSettingsContext } from "../../hooks/useSettingsContext";
import { useAccount } from "../../hooks/useAccount";
import { useTheme } from "@react-navigation/native";
import { createCommonStyles } from "../../styles/commonStyles";
import { useTranslation } from "react-i18next";
import { DEFAULTPROFILE } from "../../img/DefaultProfile";
import { LineChart, BarChart, PieChart } from 'react-native-chart-kit';
import OrgChartScreen from "../SelfService/OrgChart/OrgChartScreen";
//#endregion

const HomePageDashboard = () => {
  //#region Const
  const { colors } = useTheme();
  const { width, height } = useWindowDimensions();
  const { t } = useTranslation();
  const { getOrgChart } = useAccount();
  const { team, account, orgChart } = useAccountContext();

  const { isAlphaMega } = useSettingsContext();
  const styles = makeStyles(colors, height, width);
  const common = createCommonStyles(colors);
  const navigation = useNavigation();

  const teamNumColumns = (Platform.OS === "web" ? 2 : 1);
  const insets = useSafeAreaInsets();

  const route = useRoute();
  const setTopTabsHidden = route.params?.setTabBarHidden;
  const lastYRef = useRef(0);
  const lastToggleTsRef = useRef(0);
  const hiddenRef = useRef(false);

  //#endregion
  //#region UseEffect
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      try {
        localStorage.setItem("isRefresh", "true");
        localStorage.setItem("lastVisitedScreen", "HomePageDashboard");
      } catch (error) {
        console.log(error.message);
      }
    };
    if (Platform.OS === "web") {
      window.addEventListener("beforeunload", handleBeforeUnload);
      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }
  }, []);
  // { orgChart !== null && console.log(orgChart) }

  useEffect(() => {
    // * Get OrgChart On Load of DashBoard
    // * Does not need frequent loading - Every refresh of dashboard page should be enough 
    if (account !== null)
      getOrgChart(account.company).catch(console.error)
    navigation.setOptions({
      title: t("navigate:DB"),
    });
  }, []);

  //#endregion

  //#region OnScroll
  const onDashboardScroll = (e) => {
    if (Platform.OS === 'web') return;
    if (!setTopTabsHidden) return;

    const y = e.nativeEvent.contentOffset?.y ?? 0;
    const now = Date.now();

    const TOP_TOLERANCE = 10; 
    const HIDE_AFTER_Y = 20;

    // Show ONLY when fully at top
    if (hiddenRef.current && y <= TOP_TOLERANCE) {
      setTopTabsHidden(false);
      hiddenRef.current = false;
      lastToggleTsRef.current = now;
      return;
    }

    // Hide once user scrolls down 
    if (!hiddenRef.current && y > HIDE_AFTER_Y) {
      setTopTabsHidden(true);
      hiddenRef.current = true;
      lastToggleTsRef.current = now;
    }

    lastYRef.current = y;
  };
  //#endregion

  //#region Listener
  //#endregion

  //#region navigate

  const handleNavigateToApprovals = () => {
    // Create a new permissions object dynamically
    const permissions = {
      Leaves: {
        hasPermission: true,
      },
      // Add other permission categories if needed
    };

    // Navigate with dynamic parameters
    navigation.navigate("Leaves", {
      permissions, // Pass the dynamically created permissions object
      isAdmin: true,
      initialScreen: "LeaveApplicationApprovalScreen", // Specify the initial screen
    });
  };

  //#endregion

  //#region Renders
  const DrawEmployeeBubble = ({ item }) => {
    return (
      <View key={item.name} style={styles.teamItem}>
        <Image
          style={{
            width: Platform.OS === "web" ? 50 : 30,
            height: Platform.OS === "web" ? 50 : 30,
            marginRight: 10,
            borderRadius: 100,
            borderWidth: 1,
            justifyContent: "center",
          }}
          source={{
            uri: `data:image/png;base64,${item.photograph || DEFAULTPROFILE}`,
          }}
        />
        <View style={styles.columnTeam}>
          <CustomLabel value={item.name} type="MAIN_LABEL_DASHBOARD" />
          <CustomLabel value={item.jobtitle} type="MAIN_LABEL_DASHBOARD_MINOR" />
        </View>
      </View>)
  }
  const RenderTeam = ({ item }) => {
    return (
      <DrawEmployeeBubble item={item} />
    );
  };

  //* Workaround for nested lists
  const emptyData = [];

  const renderNullItem = () => null;
  //#region For Demo Purposes
  // Mock leave approval data per month
  const leaveData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        data: [10, 15, 7, 12, 20, 9],
        color: (opacity = 1) => `rgba(0, 123, 255, ${opacity})`, // optional
        strokeWidth: 2,
      }
    ],
    legend: ['Leaves Approved'],
  };


  const pieData = [
    { name: 'Engineering', leaves: 30, color: '#3498db', legendFontColor: '#333', legendFontSize: 12 },
    { name: 'HR', leaves: 10, color: '#e74c3c', legendFontColor: '#333', legendFontSize: 12 },
    { name: 'Marketing', leaves: 20, color: '#2ecc71', legendFontColor: '#333', legendFontSize: 12 },
    { name: 'Sales', leaves: 15, color: '#f1c40f', legendFontColor: '#333', legendFontSize: 12 },
  ];

  // Chart Configurations
  const chartConfig = {
    backgroundGradientFrom: '#f5faff',
    backgroundGradientTo: '#f5faff',
    color: (opacity = 1) => `rgba(33, 150, 243, ${opacity})`,
    strokeWidth: 2,
    decimalPlaces: 0,
    propsForDots: {
      r: '3',
      strokeWidth: '1',
      stroke: '#2196f3',
    },
  };
  //#endregion

  const ListFooterComponent = (
    <SafeAreaView style={[styles.mainView]}>
      {/* Leaves */}
      <SafeAreaView>
        <SafeAreaView style={styles.rowHome}>
          <SafeAreaView style={[common.cardstyle, styles.balance]}>
            <CustomLabel value={t("navigate:RM")} type="MINORTITLE" />
            <CustomLabel value={t("navigate:RMSub")} type="MINORTITLE_MUTED" />
            <SafeAreaView
              style={{
                elevation: 3,
                width: Platform.OS === "web" ? "97%" : "auto",
                alignItems: Platform.OS === "web" ? "center" : undefined,
                marginRight: Platform.OS === "web" ? 10 : 0,
              }}
            >
              <PersonalLeaveBalancesScreen
                AnnualLeaveOnly={true}
                ShowDetails={false}
              />
            </SafeAreaView>
          </SafeAreaView>
          <SafeAreaView style={[common.cardstyle, styles.upcoming]}>
            <CustomLabel value={t("common:weekLeaves")} type="MINORTITLE" />
            <CustomLabel value={t("common:weekLeavesSub")} type="MINORTITLE_MUTED" />
            <MyLeavesScreen isDashboard={true} /> 
          {/*check on app*/}
          </SafeAreaView>
        </SafeAreaView>
        <TouchableOpacity>
          <SafeAreaView style={styles.rowHome}>
            <ScrollView style={{ flex: 1, flexGrow: 1 }}>
              <Pressable onPress={handleNavigateToApprovals}>
                {/* navigates to my leaves not approvals & what task is assessment or timesheets*/}
                <SafeAreaView style={[common.cardstyle, styles.upcoming]}>
                  <CustomLabel
                    value={t("common:pendingTasks")}
                    type="MINORTITLE"
                  />
                  <CustomLabel value={t("common:pendingTasksSub")} type="MINORTITLE_MUTED" />
                  <LeaveApplicationApprovalScreen isDashboard={true} />
                </SafeAreaView>
              </Pressable>
            </ScrollView>
          </SafeAreaView>
        </TouchableOpacity>
      </SafeAreaView>
      <SafeAreaView style={[styles.margin]}>
        {orgChart !== null && Platform.OS === "web" && <OrgChartScreen orgChart={orgChart} />}
      </SafeAreaView>
      {/* for demo puproses only  */}
      {false &&
        <SafeAreaView style={[styles.upcoming]}>
          <CustomLabel value="Monthly Leave Approvals" type="MINORTITLE" />
          <LineChart
            data={leaveData}
            width={width - 32}
            height={220}
            chartConfig={chartConfig}
            bezier
            style={{ borderRadius: 8, marginBottom: 24 }}
          />
          <CustomLabel value="Leave Distribution by Department" type="MINORTITLE" />
          <PieChart
            data={pieData}
            width={width - 32}
            height={220}
            accessor="leaves"
            backgroundColor="transparent"
            paddingLeft="15"
            chartConfig={chartConfig}
            absolute
            style={{ borderRadius: 8 }}
          />
        </SafeAreaView>}
      {/* Team -- Hide for Alphamega */}
      {!isAlphaMega && (
        <SafeAreaView style={[common.cardstyle, styles.team]}>
          <CustomLabel value={t("common:TEAM")} type="MINORTITLE" />
          <View style={[styles.teamContainer, { overflow: "hidden" }]}>
            <ScrollView
              style={{ flex: 1, maxHeight: 700 }}
              nestedScrollEnabled={true}
              contentContainerStyle={{ flexGrow: 1 }}
            >
              <FlatList
                horizontal={false}
                scrollEnabled={true}
                showsHorizontalScrollIndicator={false}
                {...(teamNumColumns !== 1
                ? { columnWrapperStyle: { justifyContent: "space-between" } }
                : {})}
                data={team}
                renderItem={({ item, index }) => (
                  <RenderTeam item={item} index={index} />
                )}
                keyExtractor={(_item, index) => index.toString()}
                numColumns={teamNumColumns}
              />
            </ScrollView>
          </View>
        </SafeAreaView>
      )}
    </SafeAreaView>
  );
  //#endregion


  return (
    <SafeAreaView style={styles.scrollView}>
      <FlatList
        data={emptyData}
        renderItem={renderNullItem}
        ListFooterComponent={ListFooterComponent}
        contentContainerStyle={{
          paddingBottom: Math.max(insets.bottom + 24, 40),
        }}
        onScroll={onDashboardScroll}
        scrollEventThrottle={16}
      />
    </SafeAreaView>
  );
};

const makeStyles = (colors, _height, width) =>
  StyleSheet.create({
    mainView: {
      flex: 1,
      // marginHorizontal: '1%',
      marginTop: "1%",
      justifyContent: "space-between",
      //paddingTop: Platform.OS === 'web' && width > 1200 ? 50 : 20,
      //minWidth: 250
    },
    scrollView: {
      flex: 1,
    },
    columnTeam: {
      fontSize: Platform.OS === "web" && width < 700 ? 12 : 16,
    },

    rowHome: {
      flex: 1,
      flexDirection: Platform.OS === "web" && width > 600 ? "row" : "column",
      justifyContent:
        Platform.OS === "web" && width > 1200 ? "center" : "flex-start",
    },
    columnTeam: {
      flex: 1,
      marginRight: 20,
      flexDirection: "column",
      justifyContent:
        Platform.OS === "web" && width > 1200 ? "center" : "flex-start",
    },
    alignCenter: {
      alignItems: "center",
      justifyContent: "center",
      // TODO this is very weird to be looked at in the future
      marginLeft:
        Platform.OS === "web" && width > 1300
          ? 0
          : Platform.OS === "ios"
            ? 0
            : 30,
    },
    alignCenter: {
      alignItems: "center",
      justifyContent: "center",
      // TODO this is very weird to be looked at in the future
      marginLeft:
        Platform.OS === "web" && width > 1300
          ? 0
          : Platform.OS === "ios"
            ? 0
            : 30,
    },
    balance: {
      flex: 1,
      padding: 24,
      marginVertical: 10,
      marginHorizontal: 20,
      marginBottom: 0,
    },
    upcoming: {
      flex: Platform.OS === "web" && width > 1200 ? 3 : 1,
      padding: 24,
      marginVertical: 10,
      marginHorizontal: 20,
      marginBottom: 0,
    },

    team: {
      flex: 1,
      padding: 24,
      marginVertical: 10,
      marginHorizontal: 20,
      marginBottom: 0,
    },

    flatList: {
      flex: 1,
      width: Platform.OS === "web" && width > 1200 ? (width * 90) / 100 : width,
      padding: Platform.OS === "web" && width > 1200 ? 30 : 20,
    },
    margin: {
      margin: 20,
    },
    teamItem: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      paddingVertical: 12,
      paddingHorizontal: 14,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: colors.border_card,
      backgroundColor: colors.background,
      margin: 10,
    },
    teamContainer: {
      flex: 1,
      padding: 1,
      borderRadius: 35,
      elevation: 2,
      backgroundColor: Platform.OS === "web" ? colors.background_card : "#FFF",
      width: "100%",
      marginTop: 5,
    },

    container: {
      flexGrow: 1,               // Allows ScrollView content to fill available space
      justifyContent: 'center',  // Vertical centering
      alignItems: 'center',      // Horizontal centering
      padding: 20,
    },
    ceo: {
      fontWeight: 'bold',
      fontSize: 22,
      color: '#2c3e50',
      textAlign: 'center',
    },
    node: {
      marginLeft: 10,
      marginBottom: 12,
    },
    labelContainer: {
      paddingVertical: 4,
    },
    label: {
      fontWeight: 'bold',
      fontSize: 18,
      color: '#34495e',
    },
    children: {
      marginLeft: 15,
    },
    employee: {
      fontSize: 16,
      color: '#7f8c8d',
      marginTop: 4,
      marginLeft: 10,
    },
    row: {
      flex: 1,
      flexDirection: "row",
      alignItems: 'center',
      margin: 12,
    },
  });

export default HomePageDashboard;
